import {
  UserRole,
  type LoginCredentials,
  type LoginResponse,
  type User,
  type WhatsAppAuthRequest,
  type WhatsAppAuthResponse,
  type WhatsAppAuthStatus,
  type WhatsAppPINVerification,
} from '../../types/auth.types'

/**
 * Mock Authentication Service for Development
 *
 * This service simulates API authentication responses for local development
 * without requiring a backend server.
 *
 * Demo Credentials:
 * - Email: demo@aml-guard.com
 * - Password: demo123
 *
 * OR any valid email format with any password
 */

// Mock user data
const MOCK_USER: User = {
  id: 'demo-user-001',
  email: 'demo@aml-guard.com',
  firstName: 'Demo',
  lastName: 'User',
  role: UserRole.ADMIN,
  permissions: ['search', 'review', 'export', 'admin'],
  lastLogin: new Date().toISOString(),
  createdAt: '2024-01-01T00:00:00Z',
}

// Mock tokens
const MOCK_ACCESS_TOKEN = 'mock-access-token-' + Math.random().toString(36).substr(2, 9)
const MOCK_REFRESH_TOKEN = 'mock-refresh-token-' + Math.random().toString(36).substr(2, 9)

// Mock WhatsApp auth storage (simulates server-side storage)
interface MockPendingAuthRequest {
  phone: string
  status: 'pending' | 'authenticated'
  sessionId: string | null
  expiresAt: number
  pin: string
}

const mockPendingAuthRequests: Map<string, MockPendingAuthRequest> = new Map()

// Simulate API delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// Valid demo credentials
const DEMO_CREDENTIALS = {
  email: 'demo@aml-guard.com',
  password: 'demo123',
}

/**
 * Mock Authentication Service
 */
export const mockAuthService = {
  /**
   * Mock login - accepts demo credentials or any valid email
   */
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    // Simulate network delay
    await delay(800)

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(credentials.email)) {
      throw new Error('Invalid email format')
    }

    // Accept demo credentials or any valid email/password combination
    const isValidLogin =
      (credentials.email === DEMO_CREDENTIALS.email &&
       credentials.password === DEMO_CREDENTIALS.password) ||
      (emailRegex.test(credentials.email) && credentials.password.length > 0)

    if (!isValidLogin) {
      throw new Error('Invalid credentials')
    }

    // Return mock tokens and user
    const response: LoginResponse = {
      access_token: MOCK_ACCESS_TOKEN,
      refresh_token: MOCK_REFRESH_TOKEN,
      user: {
        ...MOCK_USER,
        email: credentials.email,
      },
    }

    return response
  },

  /**
   * Mock logout
   */
  logout: async (): Promise<void> => {
    await delay(300)
    // Mock logout always succeeds
    return
  },

  /**
   * Mock get profile
   */
  getProfile: async (): Promise<User> => {
    await delay(400)

    // Check if we have a stored email from login
    const storedEmail = localStorage.getItem('mock_user_email')

    return {
      ...MOCK_USER,
      email: storedEmail || MOCK_USER.email,
    }
  },

  /**
   * Mock refresh token
   */
  refreshToken: async (): Promise<LoginResponse> => {
    await delay(500)

    const newAccessToken = 'mock-access-token-' + Math.random().toString(36).substr(2, 9)
    const newRefreshToken = 'mock-refresh-token-' + Math.random().toString(36).substr(2, 9)

    return {
      access_token: newAccessToken,
      refresh_token: newRefreshToken,
      user: MOCK_USER,
    }
  },

  /**
   * Check if user is authenticated (same as real service)
   */
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('access_token')
    return !!token
  },

  /**
   * Get stored access token (same as real service)
   */
  getAccessToken: (): string | null => {
    return localStorage.getItem('access_token')
  },

  // ============================================
  // WhatsApp Cross-Device Authentication Methods
  // ============================================

  /**
   * Mock Request PIN - Simulates WhatsApp magic link sending
   * In development, auto-authenticates after 5 seconds to simulate user clicking link
   */
  requestPIN: async (request: WhatsAppAuthRequest): Promise<WhatsAppAuthResponse> => {
    await delay(800)

    // Validate phone format
    const phoneRegex = /^\+?[1-9]\d{6,14}$/
    if (!phoneRegex.test(request.phone.replace(/\s/g, ''))) {
      throw new Error('Invalid phone number format')
    }

    const authRequestId = 'mock-auth-' + Math.random().toString(36).substr(2, 9)
    const pin = Math.floor(100000 + Math.random() * 900000).toString()

    // Store pending auth request
    mockPendingAuthRequests.set(authRequestId, {
      phone: request.phone,
      status: 'pending',
      sessionId: null,
      expiresAt: Date.now() + 300000, // 5 minutes
      pin: pin,
    })

    // Auto-authenticate after 5 seconds in development (simulates user clicking magic link)
    setTimeout(() => {
      const authRequest = mockPendingAuthRequests.get(authRequestId)
      if (authRequest && authRequest.status === 'pending') {
        authRequest.status = 'authenticated'
        authRequest.sessionId = 'mock-session-' + Math.random().toString(36).substr(2, 9)
      }
    }, 5000)

    console.info(`[Mock WhatsApp Auth] Magic link sent to ${request.phone}`)
    console.info(`[Mock WhatsApp Auth] Auth Request ID: ${authRequestId}`)
    console.info(`[Mock WhatsApp Auth] PIN (for manual entry): ${pin}`)
    console.info(`[Mock WhatsApp Auth] Will auto-authenticate in 5 seconds...`)

    return {
      success: true,
      authRequestId,
      message: `Verification link sent to ${request.phone}`,
    }
  },

  /**
   * Mock Check Auth Status - Polling endpoint
   */
  checkAuthStatus: async (authRequestId: string): Promise<WhatsAppAuthStatus> => {
    await delay(300)

    const authRequest = mockPendingAuthRequests.get(authRequestId)

    if (!authRequest) {
      return { status: 'not_found' }
    }

    if (Date.now() > authRequest.expiresAt) {
      mockPendingAuthRequests.delete(authRequestId)
      return { status: 'expired' }
    }

    if (authRequest.status === 'authenticated') {
      const newAccessToken = 'mock-access-token-' + Math.random().toString(36).substr(2, 9)
      const newRefreshToken = 'mock-refresh-token-' + Math.random().toString(36).substr(2, 9)

      return {
        status: 'authenticated',
        sessionId: authRequest.sessionId!,
        access_token: newAccessToken,
        refresh_token: newRefreshToken,
        user: {
          ...MOCK_USER,
          email: `${authRequest.phone.replace(/\+/g, '')}@whatsapp.user`,
        },
      }
    }

    return { status: 'pending' }
  },

  /**
   * Mock Verify PIN - Manual fallback
   */
  verifyPIN: async (verification: WhatsAppPINVerification): Promise<LoginResponse> => {
    await delay(800)

    // Find auth request by phone number
    let foundAuthRequest: MockPendingAuthRequest | undefined
    let foundAuthRequestId: string | undefined

    for (const [id, request] of mockPendingAuthRequests.entries()) {
      if (request.phone === verification.phone) {
        foundAuthRequest = request
        foundAuthRequestId = id
        break
      }
    }

    if (!foundAuthRequest || !foundAuthRequestId) {
      throw new Error('No pending authentication found for this phone number')
    }

    if (Date.now() > foundAuthRequest.expiresAt) {
      mockPendingAuthRequests.delete(foundAuthRequestId)
      throw new Error('Authentication request expired. Please request a new PIN.')
    }

    if (foundAuthRequest.pin !== verification.pin) {
      throw new Error('Invalid PIN. Please try again.')
    }

    // Mark as authenticated
    foundAuthRequest.status = 'authenticated'
    foundAuthRequest.sessionId = 'mock-session-' + Math.random().toString(36).substr(2, 9)

    const newAccessToken = 'mock-access-token-' + Math.random().toString(36).substr(2, 9)
    const newRefreshToken = 'mock-refresh-token-' + Math.random().toString(36).substr(2, 9)

    return {
      access_token: newAccessToken,
      refresh_token: newRefreshToken,
      user: {
        ...MOCK_USER,
        email: `${verification.phone.replace(/\+/g, '')}@whatsapp.user`,
      },
    }
  },
}

export default mockAuthService
